import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  PlayIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const CATEGORIES = ['game', 'practice', 'drill', 'highlight', 'analysis', 'other', 'general'];

const getVideoThumbnail = (url) => {
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const videoId = url.includes('youtu.be')
      ? url.split('youtu.be/')[1]?.split('?')[0]
      : url.split('v=')[1]?.split('&')[0];
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  return null;
};

const Videos = () => {
  const { user, isCoach } = useAuth();

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamName, setTeamName] = useState('');

  useEffect(() => {
    const fetchVideosData = async () => {
      try {
        setLoading(true);

        const [videosResponse, teamsResponse] = await Promise.all([
          fetch('/api/videos', {
            headers: { 'Authorization': `Bearer ${user.token}` }
          }),
          fetch('/api/teams/my-teams', {
            headers: { 'Authorization': `Bearer ${user.token}` }
          }),
        ]);

        if (videosResponse.ok) {
          setVideos(await videosResponse.json());
        }

        if (teamsResponse.ok) {
          const teams = await teamsResponse.json();
          const primary = teams.find(t => t.id === (user.teamId || user.team_id)) || teams[0];
          setTeamName(primary?.name || '');
        }
      } catch (error) {
        console.error('Error fetching videos data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.token) {
      fetchVideosData();
    }
  }, [user?.token]);

  const [confirmState, setConfirmState] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    url: '',
    category: 'general',
    tags: '',
    isPublic: true,
  });

  const handleAddVideo = async (e) => {
    e.preventDefault();
    try {
      const videoData = {
        title: newVideo.title,
        description: newVideo.description,
        url: newVideo.url,
        category: newVideo.category,
        type: newVideo.url.includes('youtube.com') || newVideo.url.includes('youtu.be') ? 'youtube' : 
              newVideo.url.includes('vimeo.com') ? 'vimeo' : 'other',
        tags: newVideo.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        isPublic: newVideo.isPublic,
        thumbnail_url: getVideoThumbnail(newVideo.url),
        uploadedBy: user?.name || 'Unknown',
        uploadDate: new Date().toISOString().split('T')[0],
      };

      const response = await fetch('/api/videos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(videoData)
      });

      if (response.ok) {
        const createdVideo = await response.json();
        setVideos([createdVideo, ...videos]);
        setNewVideo({
          title: '',
          description: '',
          url: '',
          category: 'general',
          tags: '',
          isPublic: true,
        });
        setShowAddForm(false);
      } else {
        const errorData = await response.json();
        console.error('Error creating video:', errorData);
        toast.error('Failed to save video. Please try again.');
      }
    } catch (error) {
      console.error('Error creating video:', error);
      toast.error('Failed to save video. Please try again.');
    }
  };

  const handleUpdateVideo = async (e) => {
    e.preventDefault();
    try {
      const videoData = {
        title: newVideo.title,
        description: newVideo.description,
        url: newVideo.url,
        category: newVideo.category,
        type: newVideo.url.includes('youtube.com') || newVideo.url.includes('youtu.be') ? 'youtube' : 
              newVideo.url.includes('vimeo.com') ? 'vimeo' : 'other',
        tags: newVideo.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        isPublic: newVideo.isPublic,
        thumbnail_url: getVideoThumbnail(newVideo.url),
      };

      const response = await fetch(`/api/videos/${editingVideo.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(videoData)
      });

      if (response.ok) {
        const updatedVideo = await response.json();
        setVideos(videos.map(video => 
          video.id === editingVideo.id ? updatedVideo : video
        ));
        setEditingVideo(null);
        setNewVideo({
          title: '',
          description: '',
          url: '',
          category: 'general',
          tags: '',
          isPublic: true,
        });
        setShowAddForm(false);
      } else {
        const errorData = await response.json();
        console.error('Error updating video:', errorData);
        toast.error('Failed to update video. Please try again.');
      }
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error('Failed to update video. Please try again.');
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setNewVideo({
      title: video.title,
      description: video.description,
      url: video.url,
      category: video.category,
      tags: video.tags.join(', '),
      isPublic: video.isPublic,
    });
    setShowAddForm(true);
  };

  const handleDeleteVideo = (videoId) => {
    setConfirmState({
      title: 'Delete Video',
      message: 'Are you sure you want to delete this video? This cannot be undone.',
      isDangerous: true,
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/videos/${videoId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${user.token}` },
          });
          if (response.ok) {
            setVideos(prev => prev.filter(v => v.id !== videoId));
          } else {
            toast.error('Failed to delete video. Please try again.');
          }
        } catch (error) {
          console.error('Error deleting video:', error);
          toast.error('Failed to delete video. Please try again.');
        }
      },
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewVideo(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-gray-100 text-gray-800',
      passing: 'bg-blue-100 text-blue-800',
      shooting: 'bg-red-100 text-red-800',
      defense: 'bg-green-100 text-green-800',
      conditioning: 'bg-yellow-100 text-yellow-800',
      strategy: 'bg-purple-100 text-purple-800',
      homework: 'bg-indigo-100 text-indigo-800',
      'game-highlights': 'bg-pink-100 text-pink-800',
    };
    return colors[category] || colors.general;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6 mb-4 lg:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
              <PlayIcon className="h-6 w-6 lg:h-8 lg:w-8 text-primary-600 mr-2 lg:mr-3 flex-shrink-0" />
              <span className="truncate">Video Library</span>
            </h1>
            {teamName && (
              <p className="text-sm font-medium text-primary-600 mt-0.5">{teamName}</p>
            )}
            <p className="text-sm lg:text-base text-gray-600 mt-1">
              Share drills, homework, and educational content with your team
            </p>
          </div>
          {isCoach && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm lg:text-base w-full sm:w-auto justify-center"
            >
              <PlusIcon className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
              Add Video
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit Video Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingVideo ? 'Edit Video' : 'Add New Video'}
          </h2>
          <form onSubmit={editingVideo ? handleUpdateVideo : handleAddVideo} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={newVideo.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  name="category"
                  value={newVideo.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {CATEGORIES.map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={newVideo.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Video URL *
              </label>
              <input
                type="url"
                name="url"
                value={newVideo.url}
                onChange={handleInputChange}
                placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                name="tags"
                value={newVideo.tags}
                onChange={handleInputChange}
                placeholder="drills, passing, footwork"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isPublic"
                checked={newVideo.isPublic}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Make this video public to all team members
              </label>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingVideo(null);
                  setNewVideo({
                    title: '',
                    description: '',
                    url: '',
                    category: 'general',
                    tags: '',
                    isPublic: true,
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {editingVideo ? 'Update Video' : 'Add Video'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Videos Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {videos.map((video) => {
          const thumbnail = getVideoThumbnail(video.url);
          return (
          <div key={video.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Video Thumbnail */}
            <div className="aspect-video bg-gray-200 relative">
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ display: thumbnail ? 'none' : 'flex' }}
              >
                <PlayIcon className="h-12 w-12 text-gray-400" />
              </div>
              <div className="absolute top-2 right-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(video.category)}`}>
                  {video.category}
                </span>
              </div>
              {!video.isPublic && (
                <div className="absolute top-2 left-2">
                  <span className="px-2 py-1 text-xs font-medium bg-gray-800 text-white rounded-full">
                    Private
                  </span>
                </div>
              )}
            </div>
            
            {/* Video Info */}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                {video.title}
              </h3>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {video.description}
              </p>
              
              {/* Tags */}
              {video.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {video.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Video Stats */}
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <div className="flex items-center">
                  <EyeIcon className="h-4 w-4 mr-1" />
                  {video.views} views
                </div>
                <div>
                  {video.uploadDate}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center justify-between">
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 touch-manipulation"
                >
                  <PlayIcon className="h-4 w-4 mr-1" />
                  Watch
                </a>
                
                {isCoach && (
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => handleEditVideo(video)}
                      className="text-gray-400 hover:text-gray-600 p-1 touch-manipulation"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteVideo(video.id)}
                      className="text-gray-400 hover:text-red-600 p-1 touch-manipulation"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {videos.length === 0 && (
        <div className="text-center py-12">
          <PlayIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No videos yet</h3>
          <p className="text-gray-500 mb-4">Start building your team's video library</p>
          {isCoach && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add First Video
            </button>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

export default Videos;
